/**
 * `schemas/library.ts` — favorites and history, `spec.md` §6.6.
 *
 * Playlists are large enough to own `playlist.ts`; playback state and play events own
 * `playback.ts`. What is left here is MY CRATE and Recently Played.
 */
import * as z from 'zod';
import { CursorSchema, PageSchema, SeedParam, TotalCount, TrackIdParams, UnixSeconds, limitParam, } from './common.js';
import { TrackPageSchema, TrackSummarySchema } from './track.js';
/* ────────────────────────────────────────────────────────────────────────────
 * GET /api/favorites
 * ──────────────────────────────────────────────────────────────────────────── */
export const LIBRARY_SORTS = ['recent', 'alpha', 'random'];
export const LibrarySort = z.enum(LIBRARY_SORTS);
/**
 * §6.6 lists `sort`, `seed`, `limit`, `cursor` without restating the bounds, so the
 * catalogue's bounds (1..60, default 30) are used — the two screens render the same
 * card at the same density and a different page size between them would be a bug.
 *
 * `seed` is required for `sort=random` for the same reason as the catalogue: without it
 * paging a `RANDOM()` listing repeats and drops rows.
 */
export const FavoritesQuerySchema = z
    .object({
    sort: LibrarySort.default('recent'),
    seed: SeedParam.optional(),
    limit: limitParam(1, 60, 30),
    cursor: CursorSchema.optional(),
})
    .refine((q) => q.sort !== 'random' || q.seed !== undefined, {
    error: 'seed is required when sort=random',
    path: ['seed'],
});
/** §6.6: "→ `Page<TrackSummary>` with `total`" — so `total` is required, not optional. */
export const FavoritesResponseSchema = TrackPageSchema.extend({ total: TotalCount });
/* ────────────────────────────────────────────────────────────────────────────
 * PUT / DELETE /api/favorites/:trackId
 *
 * Both are idempotent, so both are safe to retry from a flaky mobile connection —
 * which is why the response states the resulting state rather than "created"/"deleted".
 * ──────────────────────────────────────────────────────────────────────────── */
export const FavoriteParamsSchema = TrackIdParams;
export const FavoriteAddedResponseSchema = z.strictObject({
    favorite: z.literal(true),
    addedAt: UnixSeconds,
});
export const FavoriteRemovedResponseSchema = z.strictObject({
    favorite: z.literal(false),
});
/* ────────────────────────────────────────────────────────────────────────────
 * GET /api/history
 * ──────────────────────────────────────────────────────────────────────────── */
export const HistoryQuerySchema = z.object({
    limit: limitParam(1, 100, 50),
    cursor: CursorSchema.optional(),
});
/**
 * Distinct tracks, newest play first. `lastPlayedAt` is the `MAX(p.started_at)` the
 * §6.6 query selects as `last_at` — it is the row's sort key, so it must travel with
 * the row or the client cannot build the next cursor.
 *
 * Note this shadows `TrackSummary.postedAt`/`stats.lastPlayedAt` conceptually but not
 * structurally: this is *this user's* last play, not the global one.
 */
export const HistoryItemSchema = TrackSummarySchema.extend({
    lastPlayedAt: UnixSeconds,
});
export const HistoryResponseSchema = PageSchema(HistoryItemSchema);
//# sourceMappingURL=library.js.map